import React from "react";
import { ANGELIKA_CINEMAS, READING_CINEMAS, TYPE_VOUCHER_ADULT } from "@config/config";
import BookingDetails from "@components/Bookings/BookingDetails/index.js";
import CalendarPicker from "@components/CalendarPicker";
import MoviePoster from "@components/MoviePoster/index.js";
import SessionDetails from "@components/SessionDetails/index.js";
import SessionSummary from "@components/SessionSummary";
import VoucherList from "@components/VoucherList";


const VoucherSessionConfirmation = ({
                                 purchasedSession,
                                 purchasedTicket,
                                 lastPurchase,
                                 formattedDate,
                                 formattedTime,
                                 calendarLinks,
                                 tempAccountCreation,
                                 isBypassActive,
                                 preferredName,
                                 displayWord,
                                 ticketCount,
                             }) => {
    const displayWordLower = displayWord.toLowerCase();
    const isReadingCinemas = purchasedSession.cinema_group_id === READING_CINEMAS;
    const isAngelika = purchasedSession.cinema_group_id === ANGELIKA_CINEMAS;
    const hasMovieVouchers = lastPurchase?.vouchers?.some(
        (v) => v.voucher_code_url && v.is_candy_bar === 0,
    );
    const hasCandyBarVouchers = lastPurchase?.vouchers?.some(
        (v) => v.voucher_code_url && v.is_candy_bar === 1,
    );


    return (
        <div className="confirmation">
            <div className="confirmation__section">
                <h1 className="confirmation__title">
                    One More Step! Redeem Your Voucher for Tickets*
                </h1>
            </div>

            <div className="confirmation__section">
                <p className="paragraph">
                    Please find your {ticketCount} {displayWordLower} to{" "}
                    <span className="bold">
                        {purchasedSession.movie_title} at {formattedTime},{" "}
                        {formattedDate}, {purchasedSession.venue_name}
                    </span>
                </p>
            </div>

            <div className="confirmation__body">
                {purchasedSession && (
                    <div className="confirmation__image">
                        <MoviePoster movie={purchasedSession} size="medium" />
                    </div>
                )}

                <div className="confirmation__details">
                    {purchasedSession && <SessionDetails session={purchasedSession} />}
                    {purchasedTicket && (
                        <div className="confirmation__movie-price-details">
                            <BookingDetails ticket={purchasedTicket} session={purchasedSession} />
                        </div>
                    )}
                </div>
            </div>

            {hasMovieVouchers && (
                <div className="confirmation__section">
                    <VoucherList
                        vouchers={lastPurchase.vouchers}
                        isCandyBar={false}
                        displayWord={displayWordLower}
                    />
                </div>
            )}

            {hasCandyBarVouchers && (
                <div className="confirmation__section">
                    <p className="paragraph bold">Please find your candy bar vouchers below:</p>
                    <VoucherList
                        vouchers={lastPurchase.vouchers}
                        isCandyBar={true}
                        displayWord={displayWordLower}
                    />
                </div>
            )}

            {(!isReadingCinemas && !isAngelika) && (
                <>
                    <div className="confirmation__section">
                        <p className="paragraph">
                            *To use your voucher/s, you may either{" "}
                            <span className="italic">book online and select your seat/s</span> through
                            the cinema website OR{" "}
                            <span className="italic">
                                present your voucher/s at the cinema&#39;s box office.
                            </span>
                        </p>
                    </div>

                    <div className="confirmation__section">
                        <p className="paragraph bold">Book online and select seats:</p>
                        <ol className="ordered-list">
                            <li className="list-item">
                                Click the &#39;Select your seat&#39; button below (this will take
                                you to your chosen session)
                            </li>
                            <li className="list-item">Select your seat/s</li>
                            <li className="list-item">
                                Select &#39;Add a Voucher&#39; (enter your voucher number/s and
                                PIN/s, attached below)
                            </li>
                            <li className="list-item">
                                Follow the remaining instructions to complete your purchase (booking
                                fees apply); the cinema will send your tickets directly to your email.
                            </li>
                        </ol>
                    </div>

                    {purchasedSession?.ticket_type_id === TYPE_VOUCHER_ADULT &&
                        purchasedTicket?.booking_url && (
                            <div className="confirmation__section">
                                <div className="confirmation__button">
                                    <a
                                        className="button button__primary"
                                        href={purchasedTicket.booking_url}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Select your seat
                                    </a>
                                </div>
                            </div>
                        )}
                </>
            )}

            <div className="confirmation__section">
                <p className="paragraph bold">Present voucher/s at the cinema box office:</p>
                <ol className="ordered-list">
                    <li className="list-item">
                        Present the {displayWordLower} attached at the box office for scanning
                    </li>
                    <li className="list-item">You&#39;ll be assigned the best available seat/s</li>
                </ol>
            </div>

            <div className="confirmation__section">
                <p className="paragraph">
                    * Please note: Your seat is only secured once you redeem your {displayWordLower}{" "}
                    for a ticket either online or at the box office. If you feel the session might be
                    busy we suggest you redeem online to secure your seat.
                </p>
            </div>

            <SessionSummary
                purchasedSession={purchasedSession}
                purchasedTicket={purchasedTicket}
                formattedDate={formattedDate}
                formattedTime={formattedTime}
            />

            <div className="confirmation__section">
                <p className="paragraph">
                    Enjoy,
                    <br />
                    {preferredName}
                </p>
            </div>

            {calendarLinks && (
                <div className="confirmation__section">
                    <CalendarPicker
                        calendarLinks={calendarLinks}
                        movieTitle={purchasedSession.movie_title}
                    />
                </div>
            )}

            {tempAccountCreation && !isBypassActive && (
                <div className="confirmation__section">
                    <p className="paragraph">
                        A secondary email will be sent to complete the account creation process.
                    </p>
                </div>
            )}
        </div>
    );
};

export default VoucherSessionConfirmation;
